import React from 'react';
import { css } from '@emotion/css';
import { Input, Spin } from 'antd';
import { colorConstants } from 'config/colorConstants';
import { loginAntIcon } from 'components/organism/Login/loginSpinner';
import type { LoginViewProps } from 'components/organism/Login/loginViewProps';

// antd v4 supplies its own border, font and placeholder colours; override them
// here so the field matches the Figma input spec.
const inputClass = css`
    height: 48px;
    border-radius: 8px;
    border-color: ${colorConstants.landingBorderStrong};
    font-family: 'IBM Plex Sans', 'Helvetica Neue', Inter, sans-serif;
    font-size: 16px;
    letter-spacing: 0.16px;

    &::placeholder {
        color: ${colorConstants.filterV2IconMuted};
    }
`;

const labelClass = 'font-plex text-[16px] font-normal leading-none tracking-[0.16px] text-filterV2TextSecondary';

type Props = Pick<
    LoginViewProps,
    | 'inputOnChange'
    | 'emailRef'
    | 'emailErrorMessage'
    | 'passwordErrorMessage'
    | 'errorMessage'
    | 'loading'
    | 'loginUser'
    | 'openResetPasswordModal'
    | 'loginPageTextPart1'
    | 'loginPageTextPart2'
>;

/**
 * The 400px sign-in card of the V2 login screen (Figma "Form" node).
 */
export const LoginFormCard = ({
    inputOnChange,
    emailRef,
    emailErrorMessage,
    passwordErrorMessage,
    errorMessage,
    loading,
    loginUser,
    openResetPasswordModal,
    loginPageTextPart1,
    loginPageTextPart2,
}: Props) => {
    const submitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            loginUser();
        }
    };

    return (
        <div className="flex w-[400px] flex-col gap-10 rounded-[12px] border border-solid border-landingBorderStrong bg-white p-8">
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <p className="m-0 font-plex text-[14px] font-normal leading-[1.6] tracking-[0.14px] text-filterV2TextSecondary">
                        {loginPageTextPart1}
                    </p>
                    <p className="m-0 font-plex text-[20px] font-bold leading-normal tracking-[0.2px] text-filterV2TextPrimary">
                        {loginPageTextPart2}
                    </p>
                </div>

                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <label className={labelClass} htmlFor="login-email">
                            Email
                        </label>
                        <Input
                            id="login-email"
                            ref={emailRef}
                            placeholder="Example@email.com"
                            className={inputClass}
                            onChange={(e) => {
                                inputOnChange(e, 'email');
                            }}
                            onKeyDown={submitOnEnter}
                        />
                        {emailErrorMessage && (
                            <span className="font-plex text-[14px] text-redAlt">{emailErrorMessage}</span>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className={labelClass} htmlFor="login-password">
                            Password
                        </label>
                        <Input
                            id="login-password"
                            type="password"
                            placeholder="At least 8 characters"
                            className={inputClass}
                            onChange={(e) => {
                                inputOnChange(e, 'password');
                            }}
                            onKeyDown={submitOnEnter}
                        />
                        {passwordErrorMessage && (
                            <span className="font-plex text-[14px] text-redAlt">{passwordErrorMessage}</span>
                        )}
                    </div>
                </div>

                <p
                    className="m-0 cursor-pointer text-right font-plex text-[16px] font-normal leading-none tracking-[0.16px] text-filterV2Primary"
                    onClick={openResetPasswordModal}
                >
                    Forgot Password?
                </p>

                {errorMessage && <span className="font-plex text-[14px] text-redAlt">{errorMessage}</span>}
            </div>

            <button
                type="button"
                disabled={loading}
                onClick={loginUser}
                className="flex w-full cursor-pointer items-center justify-center rounded-[12px] border-0 bg-filterV2TextPrimary py-4 font-plex text-[20px] font-medium leading-none tracking-[0.2px] text-white disabled:cursor-not-allowed"
            >
                {loading ? <Spin indicator={loginAntIcon} style={{ color: 'white' }} /> : 'Sign in'}
            </button>
        </div>
    );
};
